import {z} from 'zod';

import {isNil} from 'ramda';
import {isNilOrEmpty, isNotNilOrEmpty} from 'ramda-adjunct';

import {MazdaClaimScopeSchema, MazdaClaimWarrantyTypeSchema} from '@dms/api/workshop';
import i18n from '@dms/i18n';

import {Nullish} from 'shared/web';

import {hasMazdaWarrantyInstallationDetails} from '../utils/hasMazdaWarrantyInstallationDetails';

export const MAZDA_CLAIM_MAINTENANCE_DONE_RIGHT = 'doneRight' as const;
export const MAZDA_CLAIM_MAINTENANCE_NOT_DONE_CORRECTLY = 'notDoneCorrectly' as const;

const addRequiredIssue = (ctx: z.RefinementCtx, path: string) =>
  ctx.addIssue({
    code: z.ZodIssueCode.custom,
    path: [path],
    message: i18n.t('general.errors.mixed.required'),
  });

const isBlank = (value: string | Nullish) => isNilOrEmpty(value?.trim());

const isUnwritable =
  (schema: typeof MazdaClaimScopeSchema | typeof MazdaClaimWarrantyTypeSchema) =>
  (value: string | Nullish) =>
    !schema.safeParse(value).success;

const isUnwritableScope = isUnwritable(MazdaClaimScopeSchema);
const isUnwritableWarrantyType = isUnwritable(MazdaClaimWarrantyTypeSchema);

export const MazdaClaimSendFormSchema = z
  .object({
    campaignNumber: z.string().nullable().optional(),
    campaignVariantNumber: z.number().nullable().optional(),
    scope: z.string().nullable().optional(),
    warrantyType: z.string().nullable().optional(),
    mileage: z.number().min(0).nullable().optional(),
    repairDate: z.string().nullable().optional(),
    vehicleServicedAccordingToManufacturerSpecs: z
      .enum([MAZDA_CLAIM_MAINTENANCE_DONE_RIGHT, MAZDA_CLAIM_MAINTENANCE_NOT_DONE_CORRECTLY])
      .optional(),
    installationDate: z.string().nullable().optional(),
    installationMileage: z.number().min(0).nullable().optional(),
    installationRepairNo: z.string().nullable().optional(),
    isSpecialCausalPart: z.boolean(),
    causalPart: z.string().nullable().optional(),
    specialCausalPart: z.string().nullable().optional(),
    message: z.string().nullable().optional(),
  })
  .superRefine((data, ctx) => {
    if (isNil(data.mileage)) {
      addRequiredIssue(ctx, 'mileage');
    }

    if (isBlank(data.repairDate)) {
      addRequiredIssue(ctx, 'repairDate');
    }

    if (isNil(data.vehicleServicedAccordingToManufacturerSpecs)) {
      addRequiredIssue(ctx, 'vehicleServicedAccordingToManufacturerSpecs');
    }

    if (isNotNilOrEmpty(data.campaignNumber)) {
      return;
    }

    if (isUnwritableScope(data.scope)) {
      addRequiredIssue(ctx, 'scope');
    }

    if (isUnwritableWarrantyType(data.warrantyType)) {
      addRequiredIssue(ctx, 'warrantyType');
    }

    if (hasMazdaWarrantyInstallationDetails(data.warrantyType)) {
      if (isBlank(data.installationDate)) {
        addRequiredIssue(ctx, 'installationDate');
      }
      if (isNil(data.installationMileage)) {
        addRequiredIssue(ctx, 'installationMileage');
      }
      if (isBlank(data.installationRepairNo)) {
        addRequiredIssue(ctx, 'installationRepairNo');
      }
    }

    if (data.isSpecialCausalPart && isBlank(data.specialCausalPart)) {
      addRequiredIssue(ctx, 'specialCausalPart');
    }

    if (!data.isSpecialCausalPart && isBlank(data.causalPart)) {
      addRequiredIssue(ctx, 'causalPart');
    }
  });

export type MazdaClaimSendFormType = z.infer<typeof MazdaClaimSendFormSchema>;
